import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {lastValueFrom, merge, of, Subject} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  startWith,
  takeUntil,
  tap
} from 'rxjs/operators';
import {FieldConfig} from '../field.interface';
import {MatDialog} from '@angular/material/dialog';
import {PaginatedSelectTableComponent} from './paginated-select-table/paginated-select-table.component';
import {NotificationService} from "../../../../services/notification.service";
import {SettingsService} from "../../../../services/settings.service";
import {fadeIn} from "../../../animations/router-animation";
import {BaseResponse, FieldRequestInput} from "../../../../store/base.entity";
import {GraphqlService} from "../../../../services/graphql.service";

@Component({
  selector: 'app-paginated-select',
  templateUrl: './paginated-select.component.html',
  styleUrls: ['./paginated-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaginatedSelectComponent),
      multi: true
    }
  ],
  animations: [fadeIn]
})
export class PaginatedSelectComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges, ControlValueAccessor {

  @Input() field: FieldConfig;
  @Input() group: UntypedFormGroup = new UntypedFormGroup({});
  @Input() disabled = false;
  @Output() fieldValue = new EventEmitter();
  @Output() allField = new EventEmitter();
  required = false;
  filterVal: any;
  noEntriesFoundLabel = 'No entry matches';
  options: any[] = [];
  page = 1;
  /* indicate search operation is in progress */
  public searching = false;
  /* control for the search input value */
  searchCtrl: UntypedFormControl = new UntypedFormControl();
  totalPages = 0;
  loading = false;
  selected: any;
  showTable = false;
  allOptions: any[] = [];
  searchOptions: any[] = [];
  scrolling = false;
  fieldKey: string;
  private incrementPageSize$: Subject<void> = new Subject<void>();
  private resetPageSize$: Subject<void> = new Subject<void>();
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private graphService: GraphqlService,
    private settings: SettingsService,
    private notificationService: NotificationService,
  ) {
  }

  onChange: any = () => {
  }
  onTouched: any = () => {
  }


  ngOnInit() {
    try {
      this.setDefault();
      this.checkRequiredFields(this.field, 'field');
      this.checkFormData();
      if (this.field?.key) {
        this.group.addControl(this.field?.key, new UntypedFormControl('', []));
        this.required = (this.group?.get(this.field?.key)?.errors !== null && this.group?.get(this.field?.key)?.errors?.['required']) || false;
        this.filterVal = (this.group?.get(this.field?.filterValueKey || '')?.value) || this.selected;
      }
    } catch (error) {
      console.error(error);
    }
  }


  ngOnChanges(): void {
    this.setDefault();
    if (this.field.key) {
      this.checkRequiredFields(this.field, 'field');
      this.allOptions = this.field?.preventConcat ? [] : this.allOptions;
      this.options = this.field?.preventConcat ? [] : this.options;
      if (this.field.fetchPolicy === 'cache-first') {
        this.resetPageSize$.next();
      }
      if (this.disabled) {
        this.group.controls[this.field.key]?.disable();
      } else {
        this.group.controls[this.field.key]?.enable();
      }
    }

  }

  ngAfterViewInit(): void {
    this.searchCtrl.valueChanges.pipe(
      filter(search => !!search),
      tap(() => this.searching = true),
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      mergeMap(() => this.getDataFromServer()),
      tap(() => this.searching = false),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      this.generateOptions(res);
    });
    setTimeout(() => {
      merge(this.incrementPageSize$.pipe(mapTo(true)),
        this.resetPageSize$.pipe(mapTo(false))).pipe(
        startWith(false),
        tap(scrolling => {
          if (scrolling) {
            this.searching = true;
          } else {
            this.loading = true;
            // this.cdr.detectChanges();
          }
        }),
        mergeMap((doIncrement) => this.getDataFromServer(doIncrement)),
        tap(() => {
          this.loading = false;
          this.searching = false;
        }),
        takeUntil(this.destroy$),
      ).subscribe((res: any) => {
        this.generateOptions(res);
      });
    }, 0);

  }

  /** Get filtered options by the initial value */
  checkFormData() {
    this.getInitialData();
    lastValueFrom(this.group.valueChanges.pipe(first(i => !!i[this.field.key]), map((g) => {
      if (g[this.field.key]) {
        this.getInitialData(g[this.field.key]);
      }
    }))).then();
  }

  getInitialData(initVal?: any) {
    const control = this.group?.controls[this.field?.key as string];
    const initValue = control?.value || this.selected || initVal;
    if (initValue) {
      if (this.settings.isArray(initValue)) {
        const customFields: any[] = [];
        const initialElement: any[] = [];
        initValue.forEach(value => {
          // customFields.push(value + '');
          customFields.push({
            fieldName: this.field?.optionValue,
            isSearchable: true,
            isSortable: false,
            operation: 'EQ',
            searchValue: value + ''
          });
          initialElement.push(value + '');

          this.getInitialDataFromServer([
            {
              fieldName: this.field?.optionValue,
              isSearchable: true,
              isSortable: false,
              operation: 'EQ',
              searchValue: value + ''
            }
          ]);
        });
        control?.setValue(initialElement);
      } else {


        control?.setValue(initValue + '');
        this.getInitialDataFromServer([{
          fieldName: this.field?.optionValue,
          isSearchable: true,
          isSortable: false,
          operation: 'EQ',
          searchValue: initValue + ''
        }]);
      }


    }
  }

  toObject(arr, item) {
    const arrLength = arr.length;
    if (arrLength == 1) {
      return item[arr[0]];
    }
    if (arrLength == 2) {
      return item[arr[0]][arr[1]];
    }
    if (arrLength == 3) {
      return item[arr[0]][arr[1]][arr[2]];
    }
  }

  /** Modifying response to match matselect options */
  getData(res: BaseResponse<any[]>) {
    const data = this.field.mapFunction ? (res?.data || []).map(this.field.mapFunction) : (res?.data || []);
    return (data || []).filter(item => item).map(item => ({
      ...item,
      name: this.field?.optionName?.split(',')?.map(name => name.includes('.') ? this.toObject(name.split('.'), item) : (!!item ? item[name] : ''))?.join(' - ') || item['name'],
      optionValue: item?.value ?? item[this.field?.optionValue || ''] + ''
    }));

  }

  /** Generating Options while scrolling and searching */
  generateOptions(res: BaseResponse<any[]>) {
    if (res) {
      const optionValue = this.field?.optionValue || '';
      this.totalPages = res?.page?.numberOfPages + 1;
      const modifiedOptions = this.getData(res);
      const arr: any[] = this.allOptions.map(opt => opt[optionValue]);
      this.allOptions = this.allOptions.concat(modifiedOptions.filter((item: any) => arr.indexOf(item[optionValue]) === -1));
      if (this.searchCtrl?.value) {
        if (this.scrolling) {
          const arrSearch: any[] = this.searchOptions.map(opt => opt[optionValue]);
          this.searchOptions = this.searchOptions.concat(modifiedOptions.filter((item: any) => arrSearch.indexOf(item[optionValue]) === -1));
          this.options = this.searchOptions;
        } else {
          this.searchOptions = modifiedOptions;
          this.options = modifiedOptions;
        }
      } else {
        this.options = this.allOptions;
      }

      this.options = this.settings.sortArray(this.options, 'optionValue', 'ASC');
    }
  }

  /** Getting data from server with specified filters */
  getDataFromServer(doIncrement = false) {
    this.scrolling = doIncrement;
    this.page = doIncrement ? this.page + 1 : 1;
    if (this.page === 1 || this.totalPages > this.page) {
      let filtering: { [key: string]: any } = this.field?.noPagination ? {
        ...this.field?.additionalVariables
      } : {
        pageNumber: this.page ?? 0,
        itemsPerPage: this.field?.pageSize ?? 0,
        keyword: this.searchCtrl?.value || null,
        ...this.field?.additionalVariables
      };
      return this.graphService.queryObservable(
        {
          query: this.field?.query,
          variables: {filtering},
        },
        this.field?.fetchPolicy
      ).pipe(map(({data, errors}: any) => {
        if (errors) {
          console.error(errors);
          this.notificationService.errorMessage(errors[0]?.message);
        }
        return data ? Object.values(data)[0] : errors
      }), catchError(error => {
        this.loading = false;
        this.searching = false;
        console.error(error);
        this.notificationService.errorMessage(error.message || error[0].message);
        return of([]);
      }));
      /* if (this.field.customVariables && Object.keys(this.field.customVariables).length > 0) {
         const newParam = this.field.customVariables;
         return this.graphService.queryObservable(
           {
             query: this.field?.query,
             variables: {inputFilter, ...newParam}
           },
           this.field?.fetchPolicy)
           .pipe(map(({data, errors}: any) => {
             if (errors) {
               console.error(errors);
               // this.notificationService.errorMessage(errors[0]?.message);
             }
             return data ? Object.values(data)[0] : errors
           }), catchError(error => {
             console.error(error);
             this.notificationService.errorMessage(error.message || error[0].message);
             return of([]);
           }));
       } else {
         return this.graphService.queryObservable(
           {
             query: this.field?.query,
             variables: {input: inputFilter}
           },
           this.field?.fetchPolicy
         ).pipe(map(({data, errors}: any) => {
           if (errors) {
             console.error(errors);
             this.notificationService.errorMessage(errors[0]?.message);
           }
           return data ? Object.values(data)[0] : errors
         }), catchError(error => {
           this.loading = false;
           this.searching = false;
           console.error(error);
           this.notificationService.errorMessage(error.message || error[0].message);
           return of([]);
         }));
       }*/

    }

    return of([]);
  }

  /**
   * Load the next page
   */
  getNextPage(): void {
    if (!this.searching) {
      this.incrementPageSize$.next();
    }
  }

  /** Generating and emitting modified value */
  fieldChange(event: any) {
    let object: any;
    this.settingValue(event.value);
    if (this.field.multiple) {
      object = this.allOptions?.filter(element => event.value.indexOf(element.optionValue) > -1) || null;
    } else {
      object = this.allOptions?.find(element => element.optionValue + '' === event.value + '') || null;
    }
    this.fieldValue.emit({value: event.value, field: this.field, object});
  }

  toggleSelectAll(selectAllValue: boolean) {
    const key = this.field?.key as string;
    if (selectAllValue) {
      const values = this.options.map(option => option.optionValue);
      if (this.group) {
        this.group?.controls[key]?.setValue(values);
      }
      this.selected = values;
      this.allField.emit({values: values, field: this.field, objects: this.options});

    } else {
      this.selected = [];
      if (this.group) {
        this.group?.controls[key]?.patchValue(null);
      }
      this.allField.emit({values: null, field: this.field, objects: []});

    }

  }

  queryFields(): FieldRequestInput[] {
    const fields: any[] = [];
    (this.field?.searchFields)?.forEach(field => {
      let value: any = {
        fieldName: field,
        isSearchable: true,
        isSortable: false,
        operation: 'LK',
        searchValue: this.searchCtrl?.value || ''
      };
      if (this.field?.sortField === field) {
        value = {
          ...value,
          isSortable: true,
          orderDirection: 'ASC'
        };
      } else {
        if (value.hasOwnProperty('isSortable')) {
          delete value.isSortable;
        }
        if (value.hasOwnProperty('orderDirection')) {
          delete value.orderDirection;
        }
      }
      fields.push(value);
    });
    return fields;
  }

  openedChange(opened) {
    if (opened) {
      this.resetPageSize$.next();
    } else {
      this.options = this.allOptions;
    }
  }

  setDefault() {
    /** Defining Default Values */
    this.field = {
      ...this.field,
      label: this.field?.label || '',
      hideRequiredMarker: this.field?.hideRequiredMarker || true,
      key: this.field?.key || 'id',
      placeholder: this.field?.placeholder || this.field?.label || '',
      multiple: this.field?.multiple || false,
      filterKey: this.field?.filterKey || '',
      dynamicPipes: this.field?.dynamicPipes || [{pipe: 'defaultCase'}, {
        pipe: 'replace',
        firstArgs: '_',
        secondArgs: ' '
      }],
      hint: this.field?.hint || '',
      validations: this.field?.validations || [],
      preventConcat: this.field?.preventConcat || false,
      pageSize: this.field?.pageSize || 10,
      value: this.settings.isNullOrUndefined(this.field?.value) ? null : this.field?.value,
      mustHaveFilters: this.field.mustHaveFilters || [],
      optionValue: this.field.optionValue || 'id',
      optionName: this.field.optionName || '',
      fetchPolicy: this.field.fetchPolicy || 'cache-first',
      searchFields: this.field.searchFields || []
    };
  }

  settingValue($event: any): void {
    this.selected = $event;
    this.onChange(this.selected);
  }

  writeValue(value: any): void {
    this.selected = value;
    this.checkFormData();
    this.fieldChange({value});
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    const key = this.field.key as string;
    if (isDisabled) {
      this.group.controls[key].disable();
    } else {
      this.group.controls[key].enable();
    }
  }

  checkRequiredFields(input, attr) {
    if (input === null) {
      console.error('Attribute \'' + attr + '\' is required');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  showTableFn() {
    if (!this.disabled) {
      this.showTable = true;
      const dialogRef = this.dialog.open(PaginatedSelectTableComponent, {
        width: '80%',
        data: {
          title: this.field.label,
          query: this.field.query,
          fields: this.queryFields(),
          multiple: this.field.multiple,
          isPaginated: true,
          tableColumns: this.field.tableColumns,
          mapFunction: this.field.mapFunction,
          noSearchFields: this.field.noSearchFields,
          mustHaveFilters: this.field.mustHaveFilters,
          optionValue: this.field.optionValue,
          initialSelection: this.field.multiple ? this.selected : [this.selected]
        }
      });

      // const sub = dialogRef.componentInstance.onSelectData.subscribe((data: any) => {
      //   // do something
      //
      // });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        this.showTable = false;
        if (result) {
          const numberOfPages = this.totalPages - 1;
          this.generateOptions({
            data: this.field.multiple ? result : [result],
            page: {numberOfPages}
          });
          this.selected = this.field.multiple ? result?.map((item: any) => item[this.field.optionValue || ''] + '') : result[this.field.optionValue || ''] + '';
          this.group.controls[this.field.key as string].patchValue(this.selected);
          this.fieldChange({value: this.selected});
        }
      });
    }


  }

  /** Get initial options from server using specified fields and generate options */
  private getInitialDataFromServer(customFields: any) {
    this.loading = true;
    this.getDataFromServer(false)
      .pipe(takeUntil(this.destroy$),
        tap(() => this.loading = false))
      .subscribe((res: any) => {
        this.generateOptions(res);
      });
  }

}
